export interface SolutionAudience {
  id: string;
  title: string;
  summary: string;
  points: string[];
}

export const solutionAudiences: SolutionAudience[] = [
  {
    id: "individual-engineers",
    title: "Individual software engineers",
    summary:
      "For engineers who want to see the shape of a system while they build it, instead of reconstructing that picture mentally from files and terminal output.",
    points: [
      "One workspace for composing, running and inspecting a project instead of switching between an editor, a terminal and separate dashboards",
      "A visual canvas of tiles that stays connected to real, executable components rather than a diagram that drifts out of date",
      "Execution and logs available next to the structure that produced them",
    ],
  },
  {
    id: "product-engineering-teams",
    title: "Product engineering teams",
    summary:
      "For teams shipping product features who lose time re-explaining how services and components fit together across the team.",
    points: [
      "A shared visual reference for how a system's components connect, reducing time spent onboarding or re-deriving architecture from code",
      "Reusable components intended to reduce duplicated integration work across features",
      "A single place to trace a change from composition through to execution",
    ],
  },
  {
    id: "platform-engineering-teams",
    title: "Platform engineering teams",
    summary:
      "For teams responsible for the tooling, environments and internal standards other engineers build on top of.",
    points: [
      "A component and workspace model designed to support internal standards without every team reinventing them",
      "Deployment and environment tracking intended to give platform teams visibility as it matures",
      "Policy-aware architecture that platform teams can build governance on top of as capabilities land",
    ],
  },
  {
    id: "ai-assisted-teams",
    title: "AI-assisted development teams",
    summary:
      "For teams incorporating AI assistance into engineering work who need clear boundaries around what an agent can see and do.",
    points: [
      "A typed, permissioned interface for AI agents rather than unstructured browser or terminal automation",
      "Explicit confirmation and audit boundaries around agent-initiated actions",
      "A visual canvas that gives both humans and agents a shared, inspectable set of tiles to work from",
    ],
  },
  {
    id: "enterprise-governance",
    title: "Enterprises with governance requirements",
    summary:
      "For organisations that need software composition to be auditable and access-controlled, not just fast.",
    points: [
      "Organisation-scoped identity and role-aware permissions as a foundation for team and enterprise access",
      "A policy-first, deny-by-default authorization model rather than ad hoc permission checks",
      "Honest status reporting: enterprise capabilities are labelled by their real availability, not marketing language",
    ],
  },
  {
    id: "creators-and-indie-hackers",
    title: "Creators and indie hackers",
    summary:
      "For people building a product alone or in a small team, who don't have the headcount to build auth, billing and dashboards before testing whether the idea itself works.",
    points: [
      "Trusted, already-solved pieces (sign-in, storage, notifications), so effort goes into the part that's actually yours",
      "A visual canvas that stays honest about what's real today, not a demo that only looks finished",
      "No pressure to become a platform specialist before you can ship a first version",
    ],
  },
  {
    id: "beginners-and-students",
    title: "Beginners and students",
    summary:
      "For people learning how software is actually built, who shouldn't need years of rebuilding solved problems before working on anything genuinely new.",
    points: [
      "The same trusted foundation an experienced engineer uses, at whatever depth you're ready to look",
      "Components you can inspect, not just call, a real place to learn how a working system is put together",
      "You don't need to understand the whole architecture to compose something real",
    ],
  },
];
